import { useState, useRef, useEffect, forwardRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight, Menu, X } from 'lucide-react';

/* ──────────────────────────────────────────────────────────────
   Shared Navbar — used by BOTH the main page (/) and the landing
   page (/custom-data-collection). Renders identical JSX; the only
   difference is that on the landing page every nav item navigates
   back to the main page with a hash (/#why-india, /#difference…)
   instead of scrolling in place. Keeps the exact same fonts,
   classes, structure, dropdown, and mobile menu.
   ────────────────────────────────────────────────────────────── */

type NavbarProps = {
  /** The main-page activeNav state (driven by IntersectionObserver).
   *  Ignored on the landing page — there, "Custom Data Collection"
   *  is always active. */
  activeNav?: string;
  /** Main page scroll-to-id handler. Required when we're on "/".
   *  On the landing page we ignore it and use Link navigation. */
  onScrollToId?: (id: string) => void;
};

const Navbar = forwardRef<HTMLDivElement, NavbarProps>(function Navbar(
  { activeNav = '', onScrollToId },
  ref,
) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isLanding = location.pathname === '/custom-data-collection';
  // Both /audio-company-india (canonical) and /about (legacy) render the same page.
  const isAbout = location.pathname === '/audio-company-india' || location.pathname === '/about';
  const isHome = location.pathname === '/';
  const innerRef = useRef<HTMLDivElement>(null);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (innerRef.current && !innerRef.current.contains(event.target as Node)) {
        setIsMobileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Active-item resolver — on landing page "Custom Data Collection" is pinned active;
  // on /about, "About Us" is pinned active.
  const isActive = (id: string) => {
    if (isLanding) return id === 'process';
    if (isAbout) return id === 'about';
    return activeNav === id;
  };

  // Sections that live on the homepage
  const goToHomeSection = (id: string) => {
    if (isHome) onScrollToId?.(id);
    else navigate(`/#${id}`);
  };

  // Sections that live on /audio-company-india (How We Work = #why-india,
  // What We've Produced = #overview).
  const goToAbout = (hash?: string) => {
    if (isAbout) {
      onScrollToId?.(hash ?? 'hero');
    } else {
      navigate(hash ? `/audio-company-india/#${hash}` : '/audio-company-india/');
    }
  };

  const handleLogoClick = () => {
    if (isHome) onScrollToId?.('hero');
    else navigate('/');
  };

  const handleMobileItemClick = (action: () => void) => {
    setIsMobileMenuOpen(false);
    setTimeout(action, 400);
  };

  return (
    <div
      ref={ref ?? innerRef}
      className="fixed z-[100] pt-4 md:pt-[clamp(16px,1.39vw,24px)] px-4 md:px-[var(--px-container)] w-full max-w-[var(--max-content)] left-1/2 -translate-x-1/2 flex flex-col items-center gap-6"
    >
      <nav className="gradient-border relative flex items-center justify-between px-2 md:px-[clamp(6px,0.46vw,8px)] py-2 md:py-[clamp(6px,0.46vw,8px)] w-full glass-panel rounded-full bg-[#0A0A0A]/80 backdrop-blur-2xl shadow-2xl">
        <div
          className="flex items-center pl-4 md:pl-[clamp(16px,1.39vw,24px)] group cursor-pointer"
          onClick={handleLogoClick}
        >
          <img
            src="/voqals-logo.svg"
            alt="Voqals Logo"
            width="85"
            height="24"
            className="h-6 md:h-[clamp(16px,1.39vw,24px)] w-auto"
            referrerPolicy="no-referrer"
          />
        </div>
        <div className="hidden md:flex items-center gap-[clamp(16px,1.85vw,32px)] text-[clamp(10px,0.69vw,13px)] font-bold tracking-widest uppercase text-white/40">
          <button
            onClick={() => goToHomeSection('difference')}
            className={`transition-colors ${isActive('difference') ? 'text-white' : 'hover:text-white'}`}
          >
            The Voqals Advantage
          </button>
          <div className="relative group/nav">
            <button
              onClick={() => goToAbout()}
              className={`inline-block transition-colors ${isActive('about') ? 'text-white' : 'hover:text-white'} py-4`}
            >
              India's Audio Company
            </button>
            <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2 opacity-0 invisible group-hover/nav:opacity-100 group-hover/nav:visible transition-all duration-300">
              <div className="flex flex-col gap-[clamp(4px,0.46vw,8px)] bg-[#0A0A0A]/90 backdrop-blur-xl border border-white/10 rounded-2xl p-[clamp(8px,0.93vw,16px)] shadow-2xl min-w-[clamp(140px,11.57vw,200px)]">
                <button
                  onClick={() => goToAbout('why-india')}
                  className="text-left text-white/60 hover:text-[#56CFFC] transition-colors py-[clamp(4px,0.46vw,8px)] px-[clamp(4px,0.46vw,8px)] rounded-lg hover:bg-white/5"
                >
                  How We Work
                </button>
                <button
                  onClick={() => goToAbout('overview')}
                  className="text-left text-white/60 hover:text-[#56CFFC] transition-colors py-[clamp(4px,0.46vw,8px)] px-[clamp(4px,0.46vw,8px)] rounded-lg hover:bg-white/5"
                >
                  What We've Produced
                </button>
              </div>
            </div>
          </div>
          <button
            onClick={() => {
              if (isLanding) window.scrollTo({ top: 0, behavior: 'smooth' });
              else navigate('/custom-data-collection/');
            }}
            className={`transition-colors ${isActive('process') ? 'text-white' : 'hover:text-white'}`}
          >
            Custom Data Collection
          </button>
          <button
            onClick={() => goToHomeSection('datasets')}
            className={`transition-colors ${isActive('datasets') ? 'text-white' : 'hover:text-white'}`}
          >
            Ready To Use Datasets
          </button>
        </div>
        <div className="flex items-center gap-4 pr-2 md:pr-0">
          <button
            onClick={() => goToHomeSection('contact')}
            className="hidden md:flex group relative px-[clamp(16px,1.39vw,24px)] py-[clamp(8px,0.69vw,12px)] bg-white text-black rounded-full text-[clamp(10px,0.69vw,12px)] font-bold tracking-[0.15em] uppercase overflow-hidden transition-all hover:scale-105 hover:shadow-[0_0_40px_rgba(255,255,255,0.3)] items-center justify-center"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-black/5 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]" />
            <span className="relative z-10 flex items-center gap-2">Contact Us</span>
          </button>
          <button
            className="flex md:hidden p-2 text-white/80 hover:text-white transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
          >
            {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
        {/* Mobile Menu Dropdown */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -20, scale: 0.95, filter: 'blur(10px)' }}
              animate={{ opacity: 1, y: 0, scale: 1, filter: 'blur(0px)' }}
              exit={{ opacity: 0, y: -20, scale: 0.95, filter: 'blur(10px)' }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              style={{ position: 'absolute' as const }}
              className="gradient-border top-[calc(100%+0.5rem)] left-0 right-0 p-2 bg-[#050510]/95 backdrop-blur-3xl rounded-3xl shadow-[0_20px_40px_rgba(0,0,0,0.5),0_0_20px_rgba(86,207,252,0.1)] flex flex-col gap-1 overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-b from-[#1B36FF]/10 to-transparent pointer-events-none" />
              <div className="flex flex-col relative z-10">
                {[
                  { id: 'difference', label: 'The Voqals Advantage', action: () => goToHomeSection('difference') },
                  { id: 'about', label: "India's Audio Company", action: () => goToAbout() },
                  { id: 'about-how-we-work', label: '↳ How We Work', action: () => goToAbout('why-india'), indent: true },
                  { id: 'about-what-weve-produced', label: '↳ What We’ve Produced', action: () => goToAbout('overview'), indent: true },
                  { id: 'process', label: 'Custom Data Collection', action: () => navigate('/custom-data-collection/') },
                  { id: 'datasets', label: 'Ready To Use Datasets', action: () => goToHomeSection('datasets') },
                  { id: 'voice-talent', label: 'Join as Voice Talent', action: () => navigate('/voice-talent/') },
                  { id: 'talent-portal', label: '↳ Talent Portal', action: () => navigate('/voice-talent/portal'), indent: true },
                  { id: 'contact', label: 'Contact Us', action: () => goToHomeSection('contact') },
                ].map((item) => (
                  <button
                    key={item.id}
                    onClick={() => handleMobileItemClick(item.action)}
                    className={`group flex items-center justify-between text-[10px] font-bold tracking-widest uppercase ${isActive(item.id) ? 'text-white' : 'text-white/70 hover:text-white'} hover:bg-white/5 ${item.indent ? 'pl-9' : 'px-5'} py-4 rounded-2xl transition-all w-full text-left ${item.indent ? 'pr-5' : ''}`}
                  >
                    <span>{item.label}</span>
                    <ArrowRight className="w-4 h-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all text-[#56CFFC]" />
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </div>
  );
});

export default Navbar;
